import { createApp } from 'vue';
import { createPinia } from 'pinia';
import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import 'primeicons/primeicons.css';
// PHASE2
// import 'quill/dist/quill.snow.css';
import './plugins/overlayResize';
import App from './App.vue';
import router from './router';
import './styles/fonts.scss';

const creditorPortalPreset = definePreset(Aura, {
    primitive: {
        purple: {
            50: '#fbf2f8',
            100: '#f5deed',
            200: '#ebbad9',
            300: '#dc8ebe',
            400: '#c85ca0',
            500: '#b22a85',
            600: '#a61b78',
            700: '#88135f',
            800: '#6f124e',
            900: '#5d1243',
            950: '#390627',
        },
    },
    semantic: {
        primary: {
            50: '#fff5eb',
            100: '#ffe8d2',
            200: '#ffd0a7',
            300: '#ffb06f',
            400: '#fc7c19',
            500: '#f5751f',
            600: '#dd5f12',
            700: '#b74b13',
            800: '#923d15',
            900: '#763417',
            950: '#431906',
        },
        focusRing: {
            color: '{primary.color}',
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.600}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.700}',
                    activeColor: '{primary.800}',
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}',
                },
                formField: {
                    focusBorderColor: '{primary.color}',
                    floatLabelFocusColor: '{primary.600}',
                },
            },
        },
    },
    components: {
        tooltip: {
            root: {
                maxWidth: 'none',
                gutter: '4px',
                shadow: 'none',
                padding: '0',
                borderRadius: '20px',
            },
            colorScheme: {
                light: {
                    root: {
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#000000',
                    },
                },
                dark: {
                    root: {
                        background: 'rgba(255, 255, 255, 0.8)',
                        color: '#000000',
                    },
                },
            },
            css: () => `
.app-header-tooltip.p-tooltip {
    max-width: none;
}

.app-header-tooltip.p-tooltip-bottom {
    padding: 4px 0 0;
}

.app-header-tooltip.p-tooltip-top {
    padding: 0 0 4px;
}

.app-header-tooltip .p-tooltip-text {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 85px;
    width: max-content;
    height: 26px;
    padding: 0 14px;
    background: rgba(255, 255, 255, 0.8);
    color: #000000;
    box-shadow: none;
    border-radius: 20px;
    font-family: 'Archivo', sans-serif;
    font-weight: 300;
    font-size: 13px;
    line-height: 14px;
    letter-spacing: 0.03em;
    text-align: center;
    white-space: nowrap;
    word-break: normal;
}

.app-header-tooltip.p-tooltip-bottom .p-tooltip-arrow {
    border-bottom-color: rgba(255, 255, 255, 0.8) !important;
}

.app-header-tooltip.p-tooltip-top .p-tooltip-arrow {
    border-top-color: rgba(255, 255, 255, 0.8) !important;
}

.app-header-tooltip.p-tooltip-left .p-tooltip-arrow {
    border-left-color: rgba(255, 255, 255, 0.8) !important;
}

.app-header-tooltip.p-tooltip-right .p-tooltip-arrow {
    border-right-color: rgba(255, 255, 255, 0.8) !important;
}
`,
        },
    },
});

const app = createApp(App);
const pinia = createPinia();
app.use(pinia);
app.use(router);
app.use(PrimeVue, {
    locale: {
        firstDayOfWeek: 1,
    },
    theme: {
        preset: creditorPortalPreset,
        options: {
            darkModeSelector: false || 'none',
        },
    },
});

app.directive('tooltip', Tooltip);
app.directive('tooltip-app', Tooltip);

app.use(ToastService);
app.use(ConfirmationService);
app.mount('#app');
